/* =====================================================================
   Swift Ink slide cart (sections/si-cart.liquid). Replaces the AMP Slide
   Cart app.

   - Every change posts to the Ajax cart API with sections=si-cart, so the
     response already carries the re-rendered cart. Only #SiCartContent is
     swapped; express checkout buttons outside it are left alone.
   - window.SLIDECART_OPEN / SLIDECART_UPDATE / SLIDECART_CLOSE keep the AMP
     API, which every add button in the theme already calls.
   - Cart changes made by other scripts (fetch to /cart/add, /cart/change,
     /cart/update, /cart/clear) refresh the drawer; adds also open it, as
     AMP did.
   - The free shipping goal is set in GBP and converted with the shopper's
     live currency rate (Shopify.currency.rate) before it's shown. Outside
     the UK there's a second goal (data-tracked): free tracked shipping.
   - The mystery add-on card ([data-si-mystery]) toggles the £3.99 Mystery
     Tattoo in and out. When it alone would reach a shipping goal, its
     text says so.
   ===================================================================== */
(function () {
  'use strict';
  if (window.__siCart) return;

  var root = document.getElementById('SiCart');
  if (!root) return;
  window.__siCart = true;

  var SECTIONS = 'si-cart,cart-icon-bubble';
  var ROUTE = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var panel = root.querySelector('.si-cart__panel');
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isOpen = false;
  var lastFocus = null;
  var queue = Promise.resolve();

  /* ---------- Helpers ---------- */
  function content() {
    return document.getElementById('SiCartContent');
  }

  function enqueue(task) {
    queue = queue.then(task, task);
    return queue;
  }

  function money(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || root.getAttribute('data-currency') || 'GBP';
    var country = (window.Shopify && Shopify.country) || 'GB';
    var lang = (document.documentElement.lang || 'en').split('-')[0];
    try {
      return new Intl.NumberFormat(lang + '-' + country, { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + cur;
    }
  }

  function rate() {
    var r = window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate);
    return r > 0 ? r : 1;
  }

  /* A shipping fee set in GBP pence, in the shopper's currency. Outside GBP
     it's rounded up to a whole unit so checkout is never dearer than shown. */
  function fee(pence) {
    var cents = pence * rate();
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    if (cur !== 'GBP') cents = Math.ceil(cents / 100) * 100;
    return money(Math.round(cents)).replace(/[.,]00(?!\d)/, '');
  }

  function cartRequest(path, body) {
    body = body || {};
    body.sections = SECTIONS;
    body.sections_url = window.location.pathname;
    return window
      .fetch(ROUTE + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        __siCart: true
      })
      .then(function (res) {
        return res
          .json()
          .catch(function () { return {}; })
          .then(function (data) {
            if (!res.ok) {
              var err = new Error(data.description || data.message || 'Something went wrong. Please try again.');
              err.status = res.status;
              throw err;
            }
            return data;
          });
      });
  }

  /* A cart row can stand for several Shopify lines of the same design, so
     rows are tracked by data-group rather than by line key. */
  function rowId(li) {
    return li.getAttribute('data-group') || li.getAttribute('data-key');
  }

  /* ---------- Rendering ---------- */
  function snapshot() {
    var el = content();
    var snap = { fills: {}, keys: {}, stops: {}, scroll: 0, upsScroll: 0, earned: 0, shipDone: false, count: 0, mystery: false };
    if (!el) return snap;
    el.querySelectorAll('[data-si-fill]').forEach(function (f) {
      snap.fills[f.getAttribute('data-si-fill')] = parseFloat(f.style.width) || 0;
    });
    el.querySelectorAll('.si-line').forEach(function (li) {
      snap.keys[rowId(li)] = true;
    });
    var sc = el.querySelector('[data-si-scroll]');
    if (sc) snap.scroll = sc.scrollTop;
    var ut = el.querySelector('.si-ups__track');
    if (ut) snap.upsScroll = ut.scrollLeft;
    snap.earned = parseInt(el.getAttribute('data-earned'), 10) || 0;
    snap.count = parseInt(el.getAttribute('data-count'), 10) || 0;
    var ship = el.querySelector('[data-si-ship]');
    snap.shipDone = !!(ship && ship.classList.contains('is-done'));
    el.querySelectorAll('[data-si-stop].is-reached').forEach(function (st) {
      snap.stops[st.getAttribute('data-si-stop')] = true;
    });
    snap.mystery = !!el.querySelector('[data-si-mystery].is-added');
    return snap;
  }

  /* Free shipping text and bar, in the shopper's currency. Outside the UK
     the bar runs on to the free tracked goal once free shipping is reached. */
  function renderShipping(el) {
    var row = el.querySelector('[data-si-ship]');
    var valueEl = el.querySelector('[data-si-ship-value]');
    if (!row) return false;
    var goal = Math.round((parseInt(row.getAttribute('data-base'), 10) || 0) * rate());
    var tracked = Math.round((parseInt(row.getAttribute('data-tracked'), 10) || 0) * rate());
    var total = parseInt(row.getAttribute('data-total'), 10) || 0;
    var left = goal - total;
    var done = goal <= 0 || left <= 0;
    var hasTracked = tracked > goal && tracked > 0;
    var trackedDone = hasTracked && total >= tracked;
    var text = row.querySelector('[data-si-ship-text]');
    var fill = row.querySelector('[data-si-fill]');
    var html, value, finished;
    /* "free shipping", or "free tracked shipping" in the UK where the only
       tier is tracked. */
    var baseLabel = row.getAttribute('data-base-label') || 'free shipping';
    var BaseLabel = baseLabel.charAt(0).toUpperCase() + baseLabel.slice(1);
    /* One bar: to the free shipping van, or on to the free tracked van
       (with the free shipping van part-way along it). */
    var pct = hasTracked ? (total / tracked) * 100 : done ? 100 : (total / goal) * 100;
    /* Fees for the shopper's country (snippets/si-ship-estimate.liquid). */
    var saverFee = parseInt(row.getAttribute('data-fee-saver'), 10) || 0;
    var trackedFee = parseInt(row.getAttribute('data-fee-tracked'), 10) || 0;
    if (!done) {
      html = '<b>' + money(left) + '</b> away from ' + baseLabel;
      if (saverFee && trackedFee) value = 'Shipping ' + fee(saverFee) + ' Saver or ' + fee(trackedFee) + ' Tracked';
      else if (saverFee) value = 'Shipping ' + fee(saverFee);
      else value = 'Shipping calculated at checkout';
      finished = false;
    } else if (hasTracked && !trackedDone) {
      html = '<b>' + money(tracked - total) + '</b> away from free tracked shipping';
      value = trackedFee ? 'Free Saver shipping, or ' + fee(trackedFee) + ' Tracked' : 'Free shipping';
      finished = false;
    } else {
      html = '<b>' + (trackedDone ? 'Free tracked shipping' : BaseLabel) + '</b> unlocked';
      value = trackedDone ? 'Free tracked shipping' : BaseLabel;
      finished = true;
    }
    var stopBase = row.querySelector('[data-si-stop="base"]');
    var stopTracked = row.querySelector('[data-si-stop="tracked"]');
    if (stopBase) {
      stopBase.classList.toggle('is-reached', done);
      stopBase.classList.toggle('is-next', !done);
    }
    if (stopTracked) {
      stopTracked.classList.toggle('is-reached', trackedDone);
      stopTracked.classList.toggle('is-next', done && !trackedDone);
    }
    row.classList.toggle('is-partial', done && !finished);
    if (text) text.innerHTML = html;
    if (fill) fill.setAttribute('data-pct', Math.max(0, Math.min(100, pct)).toFixed(1));
    row.classList.toggle('is-done', finished);
    if (valueEl) valueEl.textContent = value;

    /* The mystery add-on alone would reach the next shipping goal: say so. */
    var mys = el.querySelector('[data-si-mystery]:not(.is-added)');
    var mysText = mys && mys.querySelector('[data-si-mystery-text]');
    if (mysText) {
      var mysPrice = parseInt(mys.getAttribute('data-price'), 10) || 0;
      var unlocks = '';
      if (!done && left <= mysPrice) unlocks = baseLabel;
      else if (hasTracked && done && !trackedDone && tracked - total <= mysPrice) unlocks = 'free tracked shipping';
      if (unlocks) mysText.innerHTML = 'And it unlocks <b>' + unlocks + '</b>.';
    }
    return finished;
  }

  /* Bars grow from where they were (or from 0 when the cart opens). */
  function animateFills(el, from) {
    el.querySelectorAll('[data-si-fill]').forEach(function (f) {
      var key = f.getAttribute('data-si-fill');
      var target = parseFloat(f.getAttribute('data-pct')) || 0;
      var start = from && key in from ? from[key] : 0;
      if (reduced) {
        f.style.width = target + '%';
        return;
      }
      f.style.transition = 'none';
      f.style.width = start + '%';
      void f.offsetWidth;
      f.style.transition = '';
      requestAnimationFrame(function () {
        f.style.width = target + '%';
      });
    });
  }

  function syncRoot(el) {
    var count = parseInt(el.getAttribute('data-count'), 10) || 0;
    root.classList.toggle('is-empty', count === 0);
  }

  /* Remember which control had focus so keyboard users keep their place. */
  function focusMemo() {
    var a = document.activeElement;
    var el = content();
    if (!a || !el || !el.contains(a)) return null;
    var li = a.closest('.si-line');
    var attr = ['data-si-qty', 'data-si-remove', 'data-si-add', 'data-si-mystery'].filter(function (n) { return a.hasAttribute(n); })[0];
    if (!attr) return null;
    return { key: li ? rowId(li) : null, attr: attr, val: a.getAttribute(attr) };
  }
  function focusRestore(memo, el) {
    if (!memo) return;
    var scope = memo.key ? el.querySelector('.si-line[data-group="' + memo.key + '"], .si-line[data-key="' + memo.key + '"]') : el;
    var sel = '[' + memo.attr + (memo.val ? '="' + memo.val + '"' : '') + ']';
    var target = (scope && scope.querySelector(sel)) || panel;
    try { target.focus({ preventScroll: true }); } catch (e) {}
  }

  function apply(sections) {
    if (!sections) return;
    var prev = snapshot();
    var memo = focusMemo();
    var html = sections['si-cart'];
    if (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.getElementById('SiCartContent');
      var cur = content();
      if (fresh && cur) {
        cur.replaceWith(fresh);
        syncRoot(fresh);
        renderShipping(fresh);
        animateFills(fresh, prev.fills);
        /* Pop the tick only on the icon that has just been reached. */
        fresh.querySelectorAll('[data-si-stop].is-reached').forEach(function (st) {
          if (!prev.stops[st.getAttribute('data-si-stop')]) st.classList.add('is-new');
        });
        var sc = fresh.querySelector('[data-si-scroll]');
        if (sc) sc.scrollTop = prev.scroll;
        var ut = fresh.querySelector('.si-ups__track');
        if (ut) ut.scrollLeft = prev.upsScroll;
        fresh.querySelectorAll('.si-line').forEach(function (li) {
          if (prev.count && !prev.keys[rowId(li)]) li.classList.add('is-new');
        });
        /* Flip the mystery card the moment it's added. */
        var mys = fresh.querySelector('[data-si-mystery].is-added');
        if (mys && !prev.mystery) mys.classList.add('is-new');
        focusRestore(memo, fresh);
      }
    }
    var bubbleHtml = sections['cart-icon-bubble'];
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      var bdoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      var src = bdoc.querySelector('.shopify-section') || bdoc.body;
      bubble.innerHTML = src.innerHTML;
      bubble.classList.remove('si-bump');
      void bubble.offsetWidth;
      bubble.classList.add('si-bump');
    }
  }

  function notify(cart) {
    document.dispatchEvent(new CustomEvent('si-cart:updated', { detail: cart || null }));
    /* Dawn's pubsub (constants.js declares PUB_SUB_EVENTS with const, so it
       isn't on window). Keeps the /cart page in step with the drawer. */
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'si-cart', cartData: cart || null });
      }
    } catch (e) {}
  }

  /* Coalesced refresh: a call made while one is in flight runs once more
     after it, so the drawer never ends on stale data. */
  var refreshing = null;
  var refreshAgain = false;
  function refresh() {
    if (refreshing) {
      refreshAgain = true;
      return refreshing;
    }
    refreshing = window
      .fetch(ROUTE + '?sections=' + SECTIONS, { headers: { Accept: 'application/json' }, __siCart: true })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        apply(sections);
        notify(null);
      })
      .catch(function () {})
      .then(function () {
        refreshing = null;
        if (refreshAgain) {
          refreshAgain = false;
          return refresh();
        }
      });
    return refreshing;
  }

  /* ---------- Open / close ---------- */
  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('si-cart-lock');
    var el = content();
    if (el) animateFills(el, null);
    setTimeout(function () {
      try { panel.focus({ preventScroll: true }); } catch (e) {}
    }, 60);
    document.dispatchEvent(new CustomEvent('si-cart:open'));
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('si-cart-lock');
    hideToast();
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('si-cart:close'));
  }

  /* ---------- Line changes ---------- */
  function lineError(li, msg) {
    var p = li.querySelector('.si-line__error');
    if (!p) return;
    p.textContent = msg;
    p.hidden = false;
  }

  function setQty(li, qty) {
    var key = li.getAttribute('data-key');
    var old = parseInt(li.getAttribute('data-line-qty') || li.getAttribute('data-qty'), 10) || 0;
    li.classList.add('is-busy');
    var errEl = li.querySelector('.si-line__error');
    if (errEl) errEl.hidden = true;
    return enqueue(function () {
      return cartRequest('cart/change.js', { id: key, quantity: qty })
        .then(function (cart) {
          apply(cart.sections);
          notify(cart);
          return true;
        })
        .catch(function (err) {
          /* Shopify re-checks stock on any quantity above 0, so a sold-out
             line can't be reduced, only removed. */
          if (err.status === 422 && qty > 0 && qty < old) {
            return cartRequest('cart/change.js', { id: key, quantity: 0 }).then(function (cart) {
              apply(cart.sections);
              notify(cart);
              toast((li.getAttribute('data-title') || 'This design') + ' sold out, so it was removed.', null);
              return true;
            });
          }
          li.classList.remove('is-busy');
          lineError(li, err.message);
          return false;
        });
    });
  }

  var undoItem = null;
  var toastTimer = null;
  function toast(text, undo) {
    var t = panel.querySelector('[data-si-toast]');
    if (!t) return;
    undoItem = undo || null;
    t.querySelector('[data-si-toast-text]').textContent = text;
    t.classList.toggle('no-undo', !undo);
    t.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, undo ? 6000 : 3500);
  }
  function hideToast() {
    var t = panel.querySelector('[data-si-toast]');
    if (t) t.hidden = true;
    undoItem = null;
  }

  function removeLine(li) {
    /* Liquid prints empty properties as [] and the add API needs an object. */
    var props = null;
    try { props = JSON.parse(li.getAttribute('data-props') || 'null'); } catch (e) {}
    var item = {
      id: parseInt(li.getAttribute('data-variant'), 10),
      quantity: parseInt(li.getAttribute('data-qty'), 10) || 1
    };
    if (props && !Array.isArray(props) && Object.keys(props).length) item.properties = props;
    var title = li.getAttribute('data-title') || 'Item';
    removeRow(li).then(function (ok) {
      if (ok) toast('Removed ' + title, item);
    });
  }

  /* Clear every Shopify line behind a row in one request. */
  function removeRow(li) {
    var keys = (li.getAttribute('data-keys') || li.getAttribute('data-key') || '').split(',').filter(Boolean);
    if (keys.length < 2) return setQty(li, 0);
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    li.classList.add('is-busy');
    return enqueue(function () {
      return cartRequest('cart/update.js', { updates: updates })
        .then(function (cart) {
          apply(cart.sections);
          notify(cart);
          return true;
        })
        .catch(function (err) {
          li.classList.remove('is-busy');
          lineError(li, err.message);
          return false;
        });
    });
  }

  /* Take the mystery add-on back out: zero every line it sits on. */
  function removeMystery(btn) {
    var updates = {};
    (btn.getAttribute('data-keys') || '').split(',').filter(Boolean).forEach(function (k) { updates[k] = 0; });
    if (!Object.keys(updates).length) return;
    btn.classList.add('is-busy');
    return enqueue(function () {
      return cartRequest('cart/update.js', { updates: updates })
        .then(function (cart) {
          apply(cart.sections);
          notify(cart);
        })
        .catch(function (err) {
          btn.classList.remove('is-busy');
          toast(err.message, null);
        });
    });
  }

  function addItems(items, btn) {
    if (btn) btn.classList.add('is-busy');
    return enqueue(function () {
      return cartRequest('cart/add.js', { items: items })
        .then(function (res) {
          if (res.sections) apply(res.sections);
          else return refresh();
          notify(null);
        })
        .catch(function (err) {
          if (btn) btn.classList.remove('is-busy');
          toast(err.message, null);
          refresh();
        });
    });
  }

  /* ---------- Events ---------- */
  root.addEventListener('click', function (e) {
    var t = e.target;
    if (t.closest('[data-si-close]')) {
      e.preventDefault();
      close();
      return;
    }
    var qtyBtn = t.closest('[data-si-qty]');
    if (qtyBtn) {
      var li = qtyBtn.closest('.si-line');
      if (!li || li.classList.contains('is-busy')) return;
      /* +/- act on the row's main line; Shopify re-splits the rest. */
      var next = (parseInt(li.getAttribute('data-line-qty') || li.getAttribute('data-qty'), 10) || 0) + parseInt(qtyBtn.getAttribute('data-si-qty'), 10);
      setQty(li, Math.max(0, next));
      return;
    }
    var rm = t.closest('[data-si-remove]');
    if (rm) {
      var line = rm.closest('.si-line');
      if (line && !line.classList.contains('is-busy')) removeLine(line);
      return;
    }
    var mys = t.closest('[data-si-mystery]');
    if (mys) {
      if (mys.classList.contains('is-busy')) return;
      if (mys.getAttribute('aria-pressed') === 'true') removeMystery(mys);
      else addItems([{ id: parseInt(mys.getAttribute('data-si-mystery'), 10), quantity: 1 }], mys);
      return;
    }
    var add = t.closest('[data-si-add]');
    if (add) {
      if (add.classList.contains('is-busy')) return;
      addItems([{ id: parseInt(add.getAttribute('data-si-add'), 10), quantity: 1 }], add);
      return;
    }
    if (t.closest('[data-si-undo]')) {
      var item = undoItem;
      hideToast();
      if (item) addItems([item], null);
      return;
    }
    var checkout = t.closest('[data-si-checkout]');
    if (checkout) {
      checkout.classList.add('is-loading');
      setTimeout(function () { checkout.classList.remove('is-loading'); }, 8000);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var focusables = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* Cart icon and any link to /cart open the drawer instead. */
  var CART_PATH = /^(?:https?:\/\/[^/]+)?(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/cart\/?(?:[?#].*)?$/i;
  document.addEventListener(
    'click',
    function (e) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
      var t = e.target;
      if (!t.closest || root.contains(t)) return;
      var trigger = t.closest('#cart-icon-bubble, .header__icon--cart, [data-cart-toggle], [data-si-cart-open]');
      if (!trigger) {
        var a = t.closest('a[href]');
        if (a && CART_PATH.test(a.getAttribute('href'))) trigger = a;
      }
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      window.SLIDECART_OPEN();
    },
    true
  );

  /* Other scripts changing the cart: refresh, and open on add (AMP did). */
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var _fetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = _fetch.apply(this, arguments);
    if ((init && init.__siCart) || !CART_MUTATION.test(url)) return p;
    p.then(function (res) {
      if (!res.ok) return;
      refresh();
      if (/\/cart\/add/.test(url)) open();
    }).catch(function () {});
    return p;
  };

  /* ---------- AMP-compatible API ---------- */
  window.SLIDECART_OPEN = function () {
    open();
    refresh();
  };
  window.SLIDECART_UPDATE = function () {
    return refresh();
  };
  window.SLIDECART_CLOSE = close;
  window.SiCart = { open: window.SLIDECART_OPEN, close: close, refresh: refresh, add: addItems };

  /* First paint of the server-rendered cart. */
  var first = content();
  if (first) {
    syncRoot(first);
    renderShipping(first);
    first.querySelectorAll('[data-si-fill]').forEach(function (f) {
      f.style.width = (parseFloat(f.getAttribute('data-pct')) || 0) + '%';
    });
  }

  /* Back/forward cache can show an old cart. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refresh();
  });
})();
